import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

/** Path (relative to `buildUrl`) for the legal documents endpoints. */
export const LEGAL_DOCUMENTS_ENDPOINT = '/legal-documents';

/**
 * Which document to fetch. These are the API's own values — the auth popup's `terms` /
 * `privacy` keys are a separate, UI-level naming (see `LEGAL_DOCUMENT_TYPE_BY_KEY`).
 */
export type LegalDocumentApiType = 'privacy_policy' | 'terms_of_service';

/** One legal document (mirrors `LegalDocumentResponseDto`). */
export type LegalDocument = {
  type: LegalDocumentApiType;
  /** Display title, e.g. "Privacy Policy". */
  title: string;
  /** Document body as an HTML fragment — no `<html>` wrapper. */
  content: string;
  /** When this version took effect (ISO-8601). */
  effectiveDate: string;
  /** When it was last edited (ISO-8601). */
  updatedAt: string;
};

/**
 * Every legal document via GET `/api/v1/legal-documents`. Authenticated with the Firebase
 * bearer token (attached by the `usKidsGolfService` auth interceptor).
 */
export async function fetchLegalDocuments(options?: RequestOptions): Promise<LegalDocument[]> {
  return await usKidsGolfService.get<LegalDocument[]>(
    buildUrl(LEGAL_DOCUMENTS_ENDPOINT),
    undefined,
    {
      skipServerErrorAlert: true,
      ...options,
    },
  );
}

/** One legal document via GET `/api/v1/legal-documents/{type}`. */
export async function fetchLegalDocument(
  type: LegalDocumentApiType,
  options?: RequestOptions,
): Promise<LegalDocument> {
  // Never alerts: the callers all fall back to the bundled copy, and the pre-auth screens
  // (Welcome / Sign up) reach this endpoint without a token and would 401.
  return await usKidsGolfService.get<LegalDocument>(
    buildUrl(`${LEGAL_DOCUMENTS_ENDPOINT}/${type}`),
    undefined,
    { skipServerErrorAlert: true, ...options },
  );
}
